
import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Mail, ArrowLeft, KeyRound, CheckCircle2 } from "lucide-react";

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const { resetPassword } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error("Por favor, informe seu e-mail");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      toast.error("E-mail Inválido", {
        description: "Favor digitar novamente"
      });
      return;
    }

    setLoading(true);
    
    try {
      // Always attempt reset; never reveal whether the email exists (anti-enumeration).
      const result = await resetPassword(email);

      if (result.error) {
        const errorMsg = result.error.message || "Verifique o e-mail informado e tente novamente.";
        toast.error(errorMsg);
      } else {
        setEmailSent(true);
        toast.success("E-mail enviado", {
          description: "Se o e-mail estiver cadastrado, você receberá instruções para redefinir sua senha."
        });
      }
    } catch (error: any) {
      toast.error("Ocorreu um erro", {
        description: error.message || "Tente novamente mais tarde."
      });
    }
    
    setLoading(false);
  };

  if (emailSent) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Navbar />
        
        <main className="flex-1 flex items-center justify-center px-4 py-24">
          <div className="max-w-md w-full">
            <div className="bg-card border border-border rounded-2xl shadow-sm p-8 md:p-10 text-center space-y-8">
              <div className="relative mx-auto w-20 h-20">
                <div className="absolute inset-0 rounded-full bg-lime/20 animate-ping opacity-20" />
                <div className="relative w-20 h-20 rounded-full bg-lime/10 border border-lime/30 flex items-center justify-center">
                  <CheckCircle2 className="h-9 w-9 text-lime" />
                </div>
              </div>
              
              <div className="space-y-3">
                <h1 className="text-3xl font-bold tracking-tight text-foreground">
                  Verifique seu e-mail
                </h1>
                <p className="text-base text-muted-foreground leading-relaxed max-w-sm mx-auto">
                  Enviamos um link de redefinição para{" "}
                  <span className="font-semibold text-foreground">{email}</span>
                </p>
                <p className="text-sm text-muted-foreground/70">
                  O link expira em 1 hora.
                </p>
              </div>
            </div>

            <div className="text-center mt-6">
              <Link 
                to="/login"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1.5"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Voltar para login
              </Link>
              <p className="text-xs text-muted-foreground/60 mt-3">
                Não recebeu? Verifique se o endereço do e-mail acima está correto e confira a pasta de spam!
              </p>
            </div>
          </div>
        </main>
        
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      
      <main className="flex-1 flex items-center justify-center px-4 py-24">
        <div className="max-w-md w-full">
          {/* Card container */}
          <div className="bg-card border border-border/40 rounded-2xl shadow-sm p-8 md:p-10 space-y-8">
            {/* Icon + Header */}
            <div className="text-center space-y-4">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto">
                <KeyRound className="h-7 w-7 text-primary" />
              </div>
              <div className="space-y-2">
                <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
                  Esqueceu sua senha?
                </h1>
                <p className="text-sm text-muted-foreground leading-relaxed max-w-xs mx-auto">
                  Sem problemas. Informe seu e-mail e enviaremos um link para criar uma nova senha.
                </p>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-foreground">
                  E-mail
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/50" />
                  <Input 
                    id="email" 
                    type="email" 
                    placeholder="seu@email.com" 
                    required 
                    className="h-12 pl-10 rounded-xl border-border/60 focus:border-primary/40 transition-colors"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>
              
              <Button 
                type="submit" 
                className="w-full h-12 rounded-xl text-sm font-semibold shadow-sm" 
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Enviando...
                  </span>
                ) : (
                  "Enviar link de redefinição"
                )}
              </Button>
            </form>
          </div>
          
          {/* Back link outside card */}
          <div className="text-center mt-6">
            <Link 
              to="/login"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1.5"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Voltar para login
            </Link>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default ForgotPasswordPage;
